// src/components/Project.tsx
// The project surface. Since the navigation rework (2026-07-27) this is NOT a
// standalone page: route /p/:id points at <Chat/>, which renders <ProjectPanel/>
// as its centre column with the sidebar and top bar still mounted. It therefore
// owns no chrome of its own (no logo strip, no "Kembali ke chat", no scroll
// container) and takes both the theme and the project id from the shell.
//
// It still guards itself Chat-style: a 401 from the API clears localStorage
// inside teamService and we redirect to /login here; a 404/403 means "this
// project is not visible to me" and returns to the chat surface.
//
// Styling follows the in-app Obsidian idiom: themeColors[theme] read through
// the `theme` prop + the global .pa-* classes.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Theme, themeColors } from '../theme/colors';
import { parseServerDate, relTime } from './Sidebar';
import {
  getProjectDetail,
  updateProject,
  deleteProject,
  getTeamMe,
  getCreditInfo,
  TeamError,
  TeamUnauthorizedError,
  ProjectRow,
  ProjectVisibility,
  SharedConversation,
} from './chat/teamService';
import {
  listLibrary,
  uploadFile,
  deleteFile,
  getDownloadUrl,
  patchFile,
  createFolder,
  deleteFolder,
  prettySize,
  fileFamily,
  childFolders,
  filesInFolder,
  joinFolder,
  parentFolder,
  FileMeta,
  FileFamily,
  FileLibrary,
  EMPTY_LIBRARY,
  FileWorkspaceError,
  UnauthorizedError,
} from './chat/fileWorkspaceService';
import { FilePreviewModal } from './chat/FilePreviewModal';

const MAX_PROJECT_NAME = 80;

// "26 Jul 2026" from a naive-UTC server timestamp.
const shortDate = (iso?: string): string => {
  if (!iso) return '';
  const d = parseServerDate(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Compact file glyph, same family split the right-panel file list uses.
const FileGlyph: React.FC<{ family: FileFamily; color: string }> = ({ family, color }) => {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: color,
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (family === 'img') {
    return (
      <svg {...common}>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    );
  }
  if (family === 'sheet') {
    return (
      <svg {...common}>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  );
};

export interface ProjectPanelProps {
  // Owned by the shell so the sidebar theme toggle re-themes this panel too.
  theme: Theme;
  // Parsed from the pathname by the shell (the route param lives on <Chat/>).
  projectId: number;
  // Anything that changes the project list or its counts: the shell refetches
  // so the sidebar section agrees with the panel without a reload.
  onDataChanged?: () => void;
}

const ProjectPanel: React.FC<ProjectPanelProps> = ({ theme, projectId, onDataChanged }) => {
  const navigate = useNavigate();
  const c = themeColors[theme];

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [conversations, setConversations] = useState<SharedConversation[]>([]);
  const [files, setFiles] = useState<FileMeta[]>([]);
  const [fileFolders, setFileFolders] = useState<string[]>([]);
  const [fileUsage, setFileUsage] = useState<FileLibrary>(EMPTY_LIBRARY);
  const [fileFolder, setFileFolder] = useState<string | null>(null);
  const [fileQuery, setFileQuery] = useState('');
  const [previewFile, setPreviewFile] = useState<FileMeta | null>(null);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [confirmDeleteFileId, setConfirmDeleteFileId] = useState<number | null>(null);
  const [confirmDeleteFolder, setConfirmDeleteFolder] = useState<string | null>(null);

  // Who am I: email decides "creator", team role decides "team owner". Both are
  // best-effort - a failure only costs the edit affordances, never the page.
  const [myEmail, setMyEmail] = useState('');
  const [isTeamOwner, setIsTeamOwner] = useState(false);

  // Header inline editor
  const [editingHeader, setEditingHeader] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftVisibility, setDraftVisibility] = useState<ProjectVisibility>('team');
  const [savingHeader, setSavingHeader] = useState(false);
  const [headerError, setHeaderError] = useState('');

  // Instructions editor
  const [draftInstructions, setDraftInstructions] = useState('');
  const [savingInstructions, setSavingInstructions] = useState(false);
  const [instructionsNotice, setInstructionsNotice] = useState('');
  const [instructionsError, setInstructionsError] = useState('');
  const instructionsRef = useRef<HTMLTextAreaElement>(null);

  // Files
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [busyDelete, setBusyDelete] = useState(false);
  const [actionError, setActionError] = useState('');
  // Inline confirm (no window.confirm anywhere in the app): the "Hapus project"
  // button itself becomes the question, with an escape hatch beside it.
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Maps any thrown error to a renderable Indonesian string. A 401 redirects
  // instead and returns '' so callers skip their own error state.
  const describe = useCallback(
    (err: unknown): string => {
      if (err instanceof TeamUnauthorizedError || err instanceof UnauthorizedError) {
        navigate('/login');
        return '';
      }
      if (err instanceof TeamError) return err.detail;
      if (err instanceof FileWorkspaceError) {
        // The upload cap answers 413; its body is not always JSON, so spell the
        // limit out rather than leaking "Request gagal (413)".
        if (err.status === 413) return 'File terlalu besar. Maksimal 15 MB per file.';
        return err.detail;
      }
      return 'Terjadi kesalahan jaringan. Coba lagi.';
    },
    [navigate]
  );

  // Loads the project detail. 404/403 = not visible to me → /team.
  const loadProject = useCallback(async (): Promise<boolean> => {
    try {
      const detail = await getProjectDetail(projectId);
      setProject(detail.project ?? null);
      setConversations(detail.conversations ?? []);
      setFiles(detail.files ?? []);
      try {
        const lib = await listLibrary(null, projectId);
        setFiles(lib.files);
        setFileFolders(lib.folders);
        setFileUsage(lib);
      } catch {
        /* detail.files already set */
      }
      setLoadError('');
      return true;
    } catch (err) {
      if (err instanceof TeamError && (err.status === 404 || err.status === 403)) {
        // Gone, or never ours. Landing on /c with no word at all read as a bug;
        // the shell turns this into a dismissible strip.
        navigate('/c', { state: { notice: 'Project tidak ditemukan.' } });
        return false;
      }
      const msg = describe(err);
      if (msg) setLoadError(msg);
      return false;
    }
  }, [projectId, describe, navigate]);

  // Initial load. No ProtectedRoute wraps this page, so the token check is ours.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!localStorage.getItem('accessToken')) {
        navigate('/login');
        return;
      }
      if (!Number.isFinite(projectId) || projectId <= 0) {
        navigate('/c', { state: { notice: 'Project tidak ditemukan.' } });
        return;
      }
      await loadProject();
      if (cancelled) return;
      setLoading(false);
      // Identity + role, best effort and independent of each other.
      getCreditInfo()
        .then((info) => {
          if (!cancelled) setMyEmail((info?.email || '').toLowerCase());
        })
        .catch(() => undefined);
      getTeamMe()
        .then((me) => {
          if (!cancelled) setIsTeamOwner(me?.role === 'owner');
        })
        .catch(() => undefined);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Keep the instructions draft in sync with whatever the server last told us,
  // but never clobber an edit in progress.
  useEffect(() => {
    if (savingInstructions) return;
    setDraftInstructions(project?.instructions ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.instructions]);

  // The instructions box starts at 3 rows and grows with its content (same
  // idea as the chat composer), so an empty project shows no dead space and a
  // long instruction set still stays readable without an inner scrollbar.
  const growInstructions = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 340)}px`;
  }, []);

  useEffect(() => {
    growInstructions(instructionsRef.current);
  }, [draftInstructions, growInstructions]);

  // Nothing to save until the draft actually differs from what was loaded.
  const instructionsDirty = draftInstructions !== (project?.instructions ?? '');

  // While a file is still extracting, poll - same 2.5s cadence as the chat's
  // file panel, and it stops the moment nothing is processing.
  const hasProcessing = files.some((f) => f.status === 'processing');
  useEffect(() => {
    if (!hasProcessing) return;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const list = await listLibrary(null, projectId);
        if (!cancelled) {
          setFiles(list.files);
          setFileFolders(list.folders);
          setFileUsage(list);
        }
      } catch {
        /* transient - the next tick retries */
      }
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [hasProcessing, projectId]);

  const creatorEmail = (project?.created_by_email || '').toLowerCase();
  // Creator or team owner may edit and delete (contract: 403 'not_project_editor'
  // for anyone else, so the server is the real gate; this only hides the UI).
  const canEdit = isTeamOwner || (!!creatorEmail && !!myEmail && creatorEmail === myEmail);

  // --- actions ------------------------------------------------------------

  const startHeaderEdit = () => {
    setDraftName(project?.name || '');
    setDraftDescription(project?.description || '');
    setDraftVisibility(project?.visibility === 'private' ? 'private' : 'team');
    setHeaderError('');
    setEditingHeader(true);
  };

  const handleSaveHeader = async () => {
    const name = draftName.trim();
    if (!name || savingHeader) return;
    setSavingHeader(true);
    setHeaderError('');
    try {
      const updated = await updateProject(projectId, {
        name,
        description: draftDescription.trim() ? draftDescription.trim() : null,
        visibility: draftVisibility,
      });
      setProject((prev) => ({ ...(prev || ({} as ProjectRow)), ...updated }));
      setEditingHeader(false);
      onDataChanged?.();
    } catch (err) {
      const msg = describe(err);
      if (msg) setHeaderError(msg);
    } finally {
      setSavingHeader(false);
    }
  };

  const handleSaveInstructions = async () => {
    if (savingInstructions) return;
    setSavingInstructions(true);
    setInstructionsError('');
    setInstructionsNotice('');
    try {
      const updated = await updateProject(projectId, {
        instructions: draftInstructions.trim() ? draftInstructions : null,
      });
      setProject((prev) => ({ ...(prev || ({} as ProjectRow)), ...updated }));
      setInstructionsNotice('Instruksi tersimpan.');
    } catch (err) {
      const msg = describe(err);
      if (msg) setInstructionsError(msg);
    } finally {
      setSavingInstructions(false);
    }
  };

  const handleDeleteProject = async () => {
    if (busyDelete) return;
    setConfirmDelete(false);
    setBusyDelete(true);
    setActionError('');
    try {
      await deleteProject(projectId);
      onDataChanged?.();
      navigate('/c');
    } catch (err) {
      const msg = describe(err);
      if (msg) setActionError(msg);
    } finally {
      setBusyDelete(false);
    }
  };

  const handleUpload = async (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    setUploading(true);
    setFileError('');
    try {
      for (let i = 0; i < picked.length; i++) {
        const meta = await uploadFile(null, picked[i], projectId, fileFolder);
        setFiles((prev) => [...prev.filter((f) => f.id !== meta.id), meta]);
      }
    } catch (err) {
      const msg = describe(err);
      if (msg) setFileError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Optimistic delete; restore the row if the server refuses (a member who did
  // not upload it gets 403, whose detail we surface inline).
  const handleDeleteFile = async (target: FileMeta) => {
    setFileError('');
    if (previewFile?.id === target.id) setPreviewFile(null);
    setFiles((prev) => prev.filter((f) => f.id !== target.id));
    try {
      await deleteFile(target.id);
    } catch (err) {
      setFiles((prev) =>
        [...prev, target].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''))
      );
      const msg = describe(err);
      if (msg) setFileError(msg);
    }
  };

  const handleDownloadFile = async (target: FileMeta) => {
    setFileError('');
    try {
      const url = await getDownloadUrl(target.id);
      if (url) window.open(url, '_blank', 'noopener');
    } catch (err) {
      const msg = describe(err);
      if (msg) setFileError(msg);
    }
  };

  // --- shared style fragments --------------------------------------------

  // Radius scale, app-wide: 6 (micro chips) / 8 (controls, rows) / 12
  // (cards, menus) / 99 (pills). Nothing else.
  const cardStyle: React.CSSProperties = {
    background: c.panel,
    border: `1px solid ${c.line}`,
    borderRadius: '12px',
    padding: '18px',
  };

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: c.ink3,
    margin: '26px 2px 10px',
  };

  const inputStyle: React.CSSProperties = {
    background: c.panel2,
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '13.5px',
    color: c.ink,
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    minWidth: 0,
  };

  const primaryBtnStyle: React.CSSProperties = {
    background: c.btnPrimaryBg,
    color: c.btnPrimaryText,
    border: '1px solid transparent',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    ['--pa-btn-hover' as any]: c.btnPrimaryHover,
  };

  // A disabled primary must not read as the loudest CTA on the page. A filled
  // panel2 chip still out-weighed the ENABLED ghost buttons beside it, so it
  // drops to a transparent base at 0.45 and sits below everything actionable.
  const primaryBtn = (disabled: boolean): React.CSSProperties =>
    disabled
      ? {
          ...primaryBtnStyle,
          background: 'transparent',
          color: c.ink3,
          border: `1px solid ${c.line2}`,
          opacity: 0.45,
          cursor: 'not-allowed',
        }
      : primaryBtnStyle;

  const ghostBtnStyle: React.CSSProperties = {
    background: 'none',
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 14px',
    fontSize: '12.5px',
    color: c.ink2,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  };

  // Ghost variant for a disabled action (e.g. "Simpan instruksi" with nothing
  // changed): same chrome, muted ink, no pointer.
  const ghostBtn = (disabled: boolean): React.CSSProperties =>
    disabled
      ? { ...ghostBtnStyle, color: c.ink3, cursor: 'not-allowed', opacity: 0.45 }
      : ghostBtnStyle;

  const monoNum: React.CSSProperties = {
    fontFamily: c.mono,
    fontVariantNumeric: 'tabular-nums',
  };

  const dangerColor = theme === 'light' ? '#DC2626' : '#F87171';

  const visibilityPill = (visibility?: ProjectVisibility) => {
    const isTeam = visibility !== 'private';
    return (
      <span
        style={{
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          padding: '3px 8px',
          borderRadius: '99px',
          // jadeStrong, not jade: at 10px uppercase the light-theme jade fails
          // AA against the panel.
          color: isTeam ? c.jadeStrong : c.ink3,
          background: isTeam ? c.jadeDim : c.panel2,
          border: `1px solid ${isTeam ? c.accentBorder : c.line2}`,
          whiteSpace: 'nowrap',
        }}
      >
        {isTeam ? 'Tim' : 'Privat'}
      </span>
    );
  };

  // --- sections -----------------------------------------------------------

  const renderSkeleton = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <div
          key={n}
          className="pa-skeleton"
          style={{
            height: n === 1 ? '104px' : '40px',
            borderRadius: '12px',
            background: c.panel,
            animationDelay: `${n * 0.12}s`,
          }}
        />
      ))}
    </div>
  );

  const renderHeaderCard = () => (
    <div style={cardStyle}>
      {editingHeader ? (
        <>
          <label
            htmlFor="project-name"
            style={{ display: 'block', fontSize: '12px', color: c.ink3, marginBottom: '6px' }}
          >
            Nama project
          </label>
          <input
            id="project-name"
            type="text"
            value={draftName}
            maxLength={MAX_PROJECT_NAME}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveHeader();
            }}
            disabled={savingHeader}
            className="pa-field"
            style={inputStyle}
          />
          <div style={{ ...monoNum, fontSize: '10.5px', color: c.ink3, marginTop: '6px' }}>
            {draftName.length}/{MAX_PROJECT_NAME}
          </div>

          <label
            htmlFor="project-description"
            style={{ display: 'block', fontSize: '12px', color: c.ink3, margin: '14px 0 6px' }}
          >
            Deskripsi
          </label>
          <textarea
            id="project-description"
            value={draftDescription}
            onChange={(e) => setDraftDescription(e.target.value)}
            rows={2}
            placeholder="Untuk apa project ini?"
            disabled={savingHeader}
            className="pa-field"
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
          />

          <label
            htmlFor="project-visibility"
            style={{ display: 'block', fontSize: '12px', color: c.ink3, margin: '14px 0 6px' }}
          >
            Siapa yang bisa melihat
          </label>
          <select
            id="project-visibility"
            value={draftVisibility}
            onChange={(e) => setDraftVisibility(e.target.value === 'private' ? 'private' : 'team')}
            disabled={savingHeader}
            className="pa-field"
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="team">Semua anggota tim</option>
            <option value="private">Hanya saya</option>
          </select>

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
            <button
              onClick={handleSaveHeader}
              disabled={savingHeader || !draftName.trim()}
              className="pa-send-btn"
              style={primaryBtn(savingHeader || !draftName.trim())}
            >
              {savingHeader ? 'Menyimpan…' : 'Simpan'}
            </button>
            <button onClick={() => setEditingHeader(false)} className="pa-ghost" style={ghostBtnStyle}>
              Batal
            </button>
          </div>
          {headerError && (
            <div style={{ marginTop: '12px', fontSize: '12.5px', color: dangerColor }}>
              {headerError}
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 650,
                color: c.ink,
                letterSpacing: '-0.01em',
                minWidth: 0,
                wordBreak: 'break-word',
              }}
            >
              {project?.name || 'Project'}
            </div>
            {visibilityPill(project?.visibility)}
            {canEdit && (
              <button
                onClick={startHeaderEdit}
                className="pa-ghost"
                style={{ ...ghostBtnStyle, marginLeft: 'auto' }}
              >
                Ubah
              </button>
            )}
          </div>

          <p style={{ margin: '10px 0 0', fontSize: '13.5px', lineHeight: 1.7, color: c.ink2 }}>
            {project?.description || 'Belum ada deskripsi.'}
          </p>

          <div style={{ fontSize: '11.5px', color: c.ink3, marginTop: '12px' }}>
            Dibuat oleh {project?.created_by_email || 'anggota tim'}
            {project?.created_at ? ` · ${shortDate(project.created_at)}` : ''}
          </div>
        </>
      )}
    </div>
  );

  const renderInstructions = () => (
    <>
      <div style={sectionLabelStyle}>Instruksi</div>
      <div style={cardStyle}>
        {canEdit ? (
          <>
            <textarea
              ref={instructionsRef}
              value={draftInstructions}
              onChange={(e) => {
                setDraftInstructions(e.target.value);
                setInstructionsNotice('');
                growInstructions(e.target);
              }}
              rows={3}
              placeholder="Contoh: Jawab selalu dalam Bahasa Indonesia, gunakan gaya bahasa santai, dan sebutkan sumbernya."
              disabled={savingInstructions}
              className="pa-field"
              style={{
                ...inputStyle,
                resize: 'none',
                overflowY: 'auto',
                minHeight: '76px',
                lineHeight: 1.7,
              }}
            />
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap' }}>
              {/* Ghost, not primary: the panel's one primary is "Chat baru di
                  project ini". Disabled until the draft actually changed. */}
              <button
                onClick={handleSaveInstructions}
                disabled={savingInstructions || !instructionsDirty}
                className="pa-ghost"
                style={ghostBtn(savingInstructions || !instructionsDirty)}
              >
                {savingInstructions ? 'Menyimpan…' : 'Simpan instruksi'}
              </button>
              {instructionsNotice && (
                <span style={{ fontSize: '12.5px', color: c.jade }}>{instructionsNotice}</span>
              )}
            </div>
            {instructionsError && (
              <div style={{ marginTop: '10px', fontSize: '12.5px', color: dangerColor }}>
                {instructionsError}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: '13px', lineHeight: 1.75, color: c.ink2, whiteSpace: 'pre-wrap' }}>
            {project?.instructions || 'Belum ada instruksi.'}
          </div>
        )}
        <div style={{ fontSize: '11.5px', color: c.ink3, marginTop: '12px', lineHeight: 1.6 }}>
          Instruksi ini otomatis dipakai AI di semua chat dalam project ini.
        </div>
      </div>
    </>
  );

  // The panel's single primary action. "Hapus project" used to sit right beside
  // it as a second same-weight button; it now lives in the danger row at the
  // bottom of the page, mirroring /team.
  const renderActions = () => (
    <div style={{ display: 'flex', gap: '8px', marginTop: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
      <button
        onClick={() => {
          // Chat's init restores `lastConversationUuid` when the route carries
          // no uuid, so without this the button would reopen the last chat
          // instead of starting a new one. Dropping both pointers is exactly
          // what the in-app "Chat baru" action does.
          localStorage.removeItem('lastConversationId');
          localStorage.removeItem('lastConversationUuid');
          // The project rides in the URL (not router state), so a reload or a
          // shared link keeps the context instead of silently dropping it.
          navigate(`/c?project=${projectId}`);
        }}
        className="pa-send-btn"
        style={primaryBtnStyle}
      >
        Chat baru di project ini
      </button>
    </div>
  );

  const renderDangerZone = () => {
    if (!canEdit) return null;
    return (
      <>
        <div style={sectionLabelStyle}>Pengaturan project</div>
        <div style={cardStyle}>
          {confirmDelete && !busyDelete ? (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                onClick={handleDeleteProject}
                className="pa-ghost"
                autoFocus
                style={{ ...ghostBtnStyle, color: dangerColor, borderColor: dangerColor, fontWeight: 600 }}
              >
                Yakin hapus?
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="pa-ghost"
                style={ghostBtnStyle}
              >
                Batal
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={busyDelete}
              className="pa-ghost"
              style={{ ...ghostBtnStyle, color: dangerColor, opacity: busyDelete ? 0.45 : 1 }}
            >
              {busyDelete ? 'Menghapus…' : 'Hapus project'}
            </button>
          )}
          <div style={{ marginTop: '8px', fontSize: '11.5px', color: c.ink3, lineHeight: 1.6 }}>
            Chat di dalam project ini tidak ikut terhapus.
          </div>
          {actionError && (
            <div style={{ marginTop: '10px', fontSize: '12.5px', color: dangerColor }}>
              {actionError}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderConversations = () => (
    <>
      <div style={sectionLabelStyle}>Chat</div>
      <div style={{ ...cardStyle, padding: '6px 14px' }}>
        {conversations.length === 0 ? (
          // Both ways in, spelled out: nobody guessed that an OLD chat could be
          // moved here, because the top bar pill is the only place that says so.
          <div style={{ padding: '14px 0', fontSize: '12.5px', color: c.ink3, lineHeight: 1.7 }}>
            Belum ada chat di project ini.
            <br />
            Mulai chat baru di sini, atau buka chat lamamu lalu tekan tombol Project di bar atas.
          </div>
        ) : (
          conversations.map((row, idx) => {
            const ownerEmail = row.owner_email || '';
            const isMine = !!ownerEmail && !!myEmail && ownerEmail.toLowerCase() === myEmail;
            const ownerLabel = isMine ? 'kamu' : ownerEmail.split('@')[0] || 'anggota tim';
            const timeLabel = relTime(row.updated_at);
            const isRunning = row.active_run_status === 'running';
            return (
              // A real <button>, so the row is keyboard reachable and reads as
              // one 44px-plus target rather than a clickable <div>.
              <React.Fragment key={row.id}>
                {idx > 0 && <div style={{ height: '1px', background: c.line }} />}
                <button
                  type="button"
                  onClick={() => navigate(`/c/${row.uuid}`)}
                  title={row.title || 'Tanpa judul'}
                  className="pa-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: 'calc(100% + 16px)',
                    marginLeft: '-8px',
                    marginRight: '-8px',
                    padding: '12px 8px',
                    minHeight: '44px',
                    background: 'none',
                    border: 'none',
                    borderRadius: '8px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: 'inherit',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        color: c.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row.title || 'Tanpa judul'}
                    </span>
                    {isRunning ? (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          fontSize: '11px',
                          color: c.jade,
                          marginTop: '4px',
                        }}
                      >
                        <span
                          style={{
                            width: '10px',
                            height: '10px',
                            border: `2px solid ${c.line2}`,
                            borderTopColor: c.jade,
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            flexShrink: 0,
                          }}
                        />
                        <span>{ownerLabel} · Sedang berjalan…</span>
                      </span>
                    ) : (
                      <span
                        style={{
                          display: 'block',
                          fontSize: '11px',
                          color: c.ink3,
                          marginTop: '4px',
                        }}
                      >
                        {timeLabel ? `${ownerLabel} · ${timeLabel}` : ownerLabel}
                      </span>
                    )}
                  </span>
                  <span style={{ color: c.ink3, fontSize: '15px', flexShrink: 0 }}>›</span>
                </button>
              </React.Fragment>
            );
          })
        )}
      </div>
    </>
  );

  const needle = fileQuery.trim().toLowerCase();
  const visibleProjectFiles = needle
    ? files.filter((f) => f.filename.toLowerCase().includes(needle))
    : filesInFolder(files, fileFolder);
  const visibleProjectFolders = needle ? [] : childFolders(fileFolders, fileFolder);

  const renderFiles = () => (
    <>
      <div style={sectionLabelStyle}>File project</div>
      <div style={{ ...cardStyle, padding: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="pa-ghost"
            style={{ ...ghostBtnStyle, opacity: uploading ? 0.45 : 1 }}
          >
            {uploading ? 'Mengunggah…' : 'Unggah file'}
          </button>
          <button
            type="button"
            onClick={() => {
              setNewFolderOpen(true);
              setNewFolderName('');
            }}
            className="pa-ghost"
            style={ghostBtnStyle}
          >
            Folder baru
          </button>
          <span style={{ fontSize: '11.5px', color: c.ink3 }}>
            Maksimal 15 MB per file, 200 file per project. File di sini dibaca AI di semua chat project ini.
          </span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => handleUpload(e.target.files)}
            style={{ display: 'none' }}
          />
        </div>
        <div style={{ marginTop: '12px', fontSize: '11px', color: c.ink3, fontFamily: c.mono }}>
          {fileUsage.file_count} / {fileUsage.max_files} file
          {fileUsage.personal_max_bytes > 0
            ? ` · penyimpananmu ${prettySize(fileUsage.personal_used_bytes)} / ${prettySize(fileUsage.personal_max_bytes)}`
            : ''}
        </div>
        {(files.length > 0 || fileFolders.length > 0) && (
          <input
            type="search"
            value={fileQuery}
            onChange={(e) => setFileQuery(e.target.value)}
            placeholder="Cari file"
            style={{ ...inputStyle, marginTop: '12px', height: '34px', padding: '0 11px' }}
          />
        )}

        {fileError && (
          <div style={{ marginTop: '10px', fontSize: '12.5px', color: dangerColor }}>{fileError}</div>
        )}

        {fileFolder && (
          <button
            type="button"
            onClick={() => setFileFolder(parentFolder(fileFolder))}
            style={{
              marginTop: '12px',
              background: 'none',
              border: 'none',
              padding: 0,
              color: c.ink2,
              fontSize: '12.5px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            ← {fileFolder}
          </button>
        )}

        {newFolderOpen && (
          <input
            autoFocus
            value={newFolderName}
            placeholder="Nama folder"
            onChange={(e) => setNewFolderName(e.target.value)}
            onBlur={() => {
              const name = newFolderName.trim();
              if (name) {
                void createFolder(joinFolder(fileFolder, name), projectId)
                  .then(() => listLibrary(null, projectId))
                  .then((lib) => {
                    setFiles(lib.files);
                    setFileFolders(lib.folders);
                    setFileUsage(lib);
                  })
                  .catch((err) => {
                    const msg = describe(err);
                    if (msg) setFileError(msg);
                  });
              }
              setNewFolderOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') setNewFolderOpen(false);
            }}
            style={{ ...inputStyle, marginTop: '10px' }}
          />
        )}

        <div style={{ marginTop: '6px' }}>
          {files.length === 0 && fileFolders.length === 0 ? (
            <div style={{ padding: '14px 0 2px', fontSize: '12.5px', color: c.ink3 }}>
              Belum ada file di project ini.
            </div>
          ) : (
            <>
              {visibleProjectFolders.map((name) => {
                const full = joinFolder(fileFolder, name);
                return (
                  <div
                    key={`folder-${full}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '11px 0',
                      borderTop: `1px solid ${c.line}`,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setFileFolder(full)}
                      style={{
                        flex: 1,
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        textAlign: 'left',
                        color: c.ink,
                        fontSize: '13px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {name}
                    </button>
                    {confirmDeleteFolder === full ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <button
                          type="button"
                          autoFocus
                          onClick={() => {
                            setConfirmDeleteFolder(null);
                            void deleteFolder(full, projectId)
                              .then(() => listLibrary(null, projectId))
                              .then((lib) => {
                                setFiles(lib.files);
                                setFileFolders(lib.folders);
                                setFileUsage(lib);
                              })
                              .catch((err) => {
                                const msg = describe(err);
                                if (msg) setFileError(msg);
                              });
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: dangerColor,
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600,
                            fontFamily: 'inherit',
                          }}
                        >
                          Yakin hapus?
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteFolder(null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: c.ink3,
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontFamily: 'inherit',
                          }}
                        >
                          Batal
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmDeleteFileId(null);
                          setConfirmDeleteFolder(full);
                        }}
                        title="Hapus folder"
                        aria-label={`Hapus folder ${name}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: dangerColor,
                          cursor: 'pointer',
                          fontSize: '16px',
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
              {visibleProjectFiles.map((f) => {
                const statusLabel =
                  f.status === 'processing' ? 'Diproses' : f.status === 'error' ? 'Gagal' : 'Siap';
                const statusColor =
                  f.status === 'error' ? dangerColor : f.status === 'processing' ? c.ink3 : c.jade;
                return (
                  <div
                    key={f.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '11px 0',
                      borderTop: `1px solid ${c.line}`,
                    }}
                  >
                    <FileGlyph family={fileFamily(f.mime, f.filename)} color={c.ink3} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {renamingId === f.id ? (
                        <input
                          autoFocus
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onBlur={() => {
                            if (renameDraft.trim() && renameDraft.trim() !== f.filename) {
                              void patchFile(f.id, { filename: renameDraft.trim() })
                                .then((row) => {
                                  setFiles((prev) => prev.map((x) => (x.id === row.id ? { ...x, ...row } : x)));
                                })
                                .catch((err) => {
                                  const msg = describe(err);
                                  if (msg) setFileError(msg);
                                });
                            }
                            setRenamingId(null);
                          }}
                          style={{ ...inputStyle, padding: '6px 8px' }}
                        />
                      ) : (
                        <button
                          onClick={() => setPreviewFile(f)}
                          onDoubleClick={() => {
                            setRenamingId(f.id);
                            setRenameDraft(f.filename);
                          }}
                          title={f.summary || 'Lihat file'}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            fontSize: '13px',
                            color: c.ink,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            display: 'block',
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textAlign: 'left',
                          }}
                        >
                          {f.filename}
                        </button>
                      )}
                      <div style={{ fontSize: '11px', color: c.ink3, marginTop: '3px' }}>
                        <span style={monoNum}>{prettySize(f.size)}</span>
                        {' · '}
                        <span style={{ color: statusColor }}>{statusLabel}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setRenamingId(f.id);
                        setRenameDraft(f.filename);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: c.ink3,
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      Ganti nama
                    </button>
                    {confirmDeleteFileId === f.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <button
                          type="button"
                          autoFocus
                          onClick={() => {
                            setConfirmDeleteFileId(null);
                            void handleDeleteFile(f);
                          }}
                          title="Hapus file ini. Tindakan ini tidak bisa dibatalkan."
                          style={{
                            background: 'none',
                            border: 'none',
                            color: dangerColor,
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600,
                            fontFamily: 'inherit',
                          }}
                        >
                          Yakin hapus?
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteFileId(null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: c.ink3,
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontFamily: 'inherit',
                          }}
                        >
                          Batal
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmDeleteFolder(null);
                          setConfirmDeleteFileId(f.id);
                        }}
                        title="Hapus file"
                        aria-label={`Hapus ${f.filename}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: dangerColor,
                          cursor: 'pointer',
                          fontSize: '16px',
                          lineHeight: 1,
                          padding: '2px 4px',
                          flexShrink: 0,
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    // No chrome of its own: the shell owns the scroll container, the top bar
    // (which carries the back chevron and the project name) and the sidebar.
    <div
      style={{
        maxWidth: '820px',
        margin: '0 auto',
        padding: '22px 18px 72px',
        color: c.ink,
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        // Interaction-state vars consumed by .pa-field / .pa-ghost / .pa-row
        // in index.css (same inline-var idiom as the composer's --pa-btn-hover).
        ['--pa-focus-ring' as any]: c.jade,
        ['--pa-focus-ring-dim' as any]: c.jadeDim,
        ['--pa-ghost-hover-bg' as any]: c.panel2,
        ['--pa-ghost-hover-line' as any]: c.line2,
        ['--pa-row-hover-bg' as any]: c.panel,
      }}
    >
        {loading ? (
          renderSkeleton()
        ) : loadError && !project ? (
          <div style={cardStyle}>
            <div style={{ fontSize: '13.5px', color: c.ink, marginBottom: '10px' }}>
              Gagal memuat project.
            </div>
            <div style={{ fontSize: '12.5px', color: c.ink2, marginBottom: '14px' }}>{loadError}</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => loadProject()} className="pa-ghost" style={ghostBtnStyle}>
                Coba lagi
              </button>
              <button onClick={() => navigate('/c')} className="pa-ghost" style={ghostBtnStyle}>
                Kembali ke chat
              </button>
            </div>
          </div>
        ) : (
          <>
            {renderHeaderCard()}
            {renderInstructions()}
            {renderActions()}
            {renderConversations()}
            {renderFiles()}
            {renderDangerZone()}
            {previewFile && (
              <FilePreviewModal
                file={previewFile}
                theme={theme}
                themeColors={themeColors}
                onClose={() => setPreviewFile(null)}
                onDownload={handleDownloadFile}
              />
            )}
          </>
        )}
    </div>
  );
};

export default ProjectPanel;
export { ProjectPanel };
